import { useEffect, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { useAgents } from "@/components/Agents/AgentsContext";
import {
  AGENT_MAIL_PROVIDER_OPTIONS,
  agentMailProviderLabel,
  agentMailProviderOption,
  agentPersonalMailIsConnected,
  agentPersonalMailIsPending,
  clearPersonalMailOAuthHandoff,
  fetchPersonalMailConfig,
  personalMailCustomerErrorMessage,
  providerUnavailableMessage,
  parsePersonalMailCallbackSearch,
  personalMailOAuthStartUrl,
  readPersonalMailOAuthHandoff,
  resolvePersonalMailStatus,
  stashPersonalMailOAuthHandoff,
  stripPersonalMailCallbackParams,
  type AgentMailProvider,
  type PersonalMailConfigResponse,
  type PersonalMailProviderConfig,
} from "@/lib/agentPersonalMail";
import {
  agentMailboxAddress,
  normalizeEmailCapabilities,
  type AgentEmailCapabilities,
  type OrgAgent,
} from "@/lib/agentsDemoData";
import type { AgentEmailSettingsPane } from "@/lib/agentNav";
import { agentsHref } from "@/lib/atwAgentsHost";

const CAPABILITY_OPTIONS: { key: keyof AgentEmailCapabilities; label: string }[] = [
  { key: "create", label: "Create" },
  { key: "draft", label: "Draft" },
  { key: "edit", label: "Edit" },
  { key: "delete", label: "Delete" },
];

function CapabilityToggles({
  value,
  disabled,
  onChange,
}: {
  value: AgentEmailCapabilities;
  disabled: boolean;
  onChange: (next: AgentEmailCapabilities) => void;
}) {
  return (
    <fieldset className="space-y-2">
      <legend className="text-xs font-semibold uppercase tracking-wide text-gray-500">
        Capabilities
      </legend>
      <p className="text-xs text-muted-foreground">
        What this agent may do with emails on this address. After connect, these start as
        everything the mail connection allows. Turn one off to restrict.
      </p>
      <div className="flex flex-wrap gap-3">
        {CAPABILITY_OPTIONS.map((option) => (
          <label
            key={option.key}
            className="inline-flex items-center gap-2 text-sm text-gray-800 dark:text-gray-200"
          >
            <input
              type="checkbox"
              checked={value[option.key]}
              disabled={disabled}
              onChange={(event) =>
                onChange({ ...value, [option.key]: event.target.checked })
              }
            />
            {option.label}
          </label>
        ))}
      </div>
    </fieldset>
  );
}

function ProviderPicker({
  picked,
  disabled,
  onPick,
  providerConfigs,
  configLoaded,
}: {
  picked: AgentMailProvider | null;
  disabled: boolean;
  onPick: (provider: AgentMailProvider) => void;
  providerConfigs: PersonalMailConfigResponse["providers"] | null;
  configLoaded: boolean;
}) {
  return (
    <ul className="space-y-2">
      {AGENT_MAIL_PROVIDER_OPTIONS.map((row) => {
        const selected = picked === row.id;
        const cfg = providerConfigs?.[row.id];
        let ready = false;
        let statusLabel = "checking…";
        if (configLoaded) {
          if (row.id === "atw_mail") {
            ready = cfg?.ready !== false;
          } else {
            ready = cfg?.ready === true;
          }
          statusLabel = ready ? "available" : "unavailable";
        }
        return (
          <li key={row.id}>
            <button
              type="button"
              disabled={disabled}
              onClick={() => onPick(row.id)}
              className={[
                "w-full rounded-md border px-3 py-3 text-left transition-colors",
                selected
                  ? "border-gray-900 bg-gray-50 dark:border-white dark:bg-white/10"
                  : "border-gray-200 hover:bg-gray-50 dark:border-white/10 dark:hover:bg-white/5",
                disabled ? "cursor-not-allowed opacity-60" : "",
              ].join(" ")}
            >
              <span className="flex items-center justify-between gap-2">
                <span className="block text-sm font-medium text-gray-900 dark:text-white">
                  {row.label}
                </span>
                <span
                  className={[
                    "shrink-0 text-[10px] font-semibold uppercase tracking-wide",
                    !configLoaded
                      ? "text-gray-500 dark:text-gray-400"
                      : ready
                        ? "text-emerald-700 dark:text-emerald-400"
                        : "text-amber-700 dark:text-amber-400",
                  ].join(" ")}
                >
                  {statusLabel}
                </span>
              </span>
              <span className="mt-0.5 block text-xs text-muted-foreground">
                {row.description}
              </span>
            </button>
          </li>
        );
      })}
    </ul>
  );
}

function ProviderConnectPanel({
  provider,
  config,
  starting,
  canEdit,
  configLoaded,
  onStart,
}: {
  provider: AgentMailProvider;
  config: PersonalMailProviderConfig | null | undefined;
  starting: boolean;
  canEdit: boolean;
  configLoaded: boolean;
  onStart: () => void;
}) {
  const option = agentMailProviderOption(provider);
  if (!option) return null;
  const unavailable = providerUnavailableMessage(provider, config, { configLoaded });
  const ready = config?.ready === true && !unavailable;

  return (
    <div className="space-y-3 rounded-md border border-gray-200 px-3 py-3 dark:border-white/10">
      <p className="text-gray-800 dark:text-gray-200">
        {provider === "atw_mail"
          ? "Connect with ATW Mail binds this agent to the mailbox on your signed-in ATW session (auth.atw.net). It is agent-owned, not an org Connections row."
          : `${option.label} starts an agent-owned connection for this agent only (not an org Connections row). After you sign in with the provider, you return to this personal email page.`}
      </p>
      {unavailable ? (
        <p className="rounded-md border border-gray-200 bg-gray-50/80 px-3 py-2 text-gray-800 dark:border-white/10 dark:bg-white/5 dark:text-gray-200">
          {unavailable}
        </p>
      ) : (
        <p className="text-xs text-muted-foreground">{option.nextStepNote}</p>
      )}
      {canEdit ? (
        <Button
          type="button"
          size="sm"
          disabled={
            starting ||
            !configLoaded ||
            Boolean(unavailable && provider !== "atw_mail")
          }
          onClick={onStart}
        >
          {starting ? "Starting…" : option.connectButtonLabel}
        </Button>
      ) : null}
      {!ready && provider !== "atw_mail" ? (
        <p className="text-xs text-muted-foreground">
          Connect stays disabled while this provider is unavailable.
        </p>
      ) : null}
    </div>
  );
}

export function AgentEmailSection({
  agent,
  pane,
}: {
  agent: OrgAgent;
  pane: AgentEmailSettingsPane;
}) {
  const {
    org,
    may,
    persistenceReady,
    persistenceSource,
    startAgentPersonalMailConnect,
    completeAgentPersonalMailConnect,
    clearAgentPersonalMail,
    setAgentSharedInboxes,
    setAgentPersonalEmailCapabilities,
    setAgentSharedInboxCapabilities,
  } = useAgents();
  const location = useLocation();
  const navigate = useNavigate();
  const canEdit = may("manage_agent_configuration");
  const mailbox = agentMailboxAddress(agent);
  const selectedShared = new Set(agent.sharedInboxIds ?? []);
  const personalCaps = normalizeEmailCapabilities(agent.personalEmailCapabilities);
  const status = resolvePersonalMailStatus(agent);
  const isConnected = agentPersonalMailIsConnected(agent);
  const isPending = agentPersonalMailIsPending(agent);
  const activeProvider = agent.personalMailProvider ?? null;

  const [pickedProvider, setPickedProvider] = useState<AgentMailProvider | null>(
    activeProvider,
  );
  const [starting, setStarting] = useState(false);
  const [mailConfig, setMailConfig] = useState<PersonalMailConfigResponse | null>(null);
  const [configLoaded, setConfigLoaded] = useState(false);
  const [oauthError, setOauthError] = useState<string | null>(null);
  const handledCallbackRef = useRef<string | null>(null);

  const pickedOption = agentMailProviderOption(pickedProvider);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const cfg = await fetchPersonalMailConfig();
      if (cancelled) return;
      setMailConfig(cfg);
      setConfigLoaded(true);
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Capture OAuth return query params immediately (before hydration can race),
  // then apply only after the roster is ready so D1 cannot overwrite connected.
  useEffect(() => {
    if (pane !== "personal") return;

    const fromUrl = parsePersonalMailCallbackSearch(location.search);
    if (fromUrl.kind !== "none") {
      stashPersonalMailOAuthHandoff(agent.id, fromUrl);
      const cleanSearch = stripPersonalMailCallbackParams(location.search);
      navigate(
        { pathname: location.pathname, search: cleanSearch },
        { replace: true },
      );
    }

    // Wait for server roster hydration so complete can flush D1 (not a local-only no-op).
    if (!persistenceReady || persistenceSource === "loading") return;

    const handoff = readPersonalMailOAuthHandoff(agent.id);
    if (!handoff) return;
    const handoffKey = `${handoff.agentId}:${handoff.at}:${handoff.result.kind}`;
    if (handledCallbackRef.current === handoffKey) return;
    handledCallbackRef.current = handoffKey;

    const result = handoff.result;
    if (result.kind === "error") {
      const message = personalMailCustomerErrorMessage(result.message);
      setOauthError(message);
      clearPersonalMailOAuthHandoff(agent.id);
      // Cancel / deny / missing Gmail scopes must never look connected.
      // Leave pending + error so Disconnect/Cancel pending is obvious; do not
      // call completeAgentPersonalMailConnect.
      toast.error(message, { duration: 8000 });
      return;
    }

    if (!canEdit) {
      toast.error("A Super Admin must finish this agent email connection.");
      return;
    }

    void (async () => {
      const saved = await completeAgentPersonalMailConnect(agent.id, {
        provider: result.provider,
        mailboxAddress: result.email,
      });
      if (!saved) {
        // Allow a real retry after the agent row exists / server sync recovers.
        handledCallbackRef.current = null;
        toast.error("Could not save this agent's email connection. Try Connect again.", {
          duration: 6000,
        });
        return;
      }
      clearPersonalMailOAuthHandoff(agent.id);
      setOauthError(null);
      setPickedProvider(result.provider);
      toast.success(
        `${agentMailProviderLabel(result.provider)} connected as ${result.email}.`,
      );
    })();
  }, [
    pane,
    location.search,
    location.pathname,
    navigate,
    canEdit,
    completeAgentPersonalMailConnect,
    agent.id,
    persistenceReady,
    persistenceSource,
  ]);

  const startConnect = (provider: AgentMailProvider) => {
    if (!canEdit) return;
    const cfg = mailConfig?.providers?.[provider];
    const unavailable = providerUnavailableMessage(provider, cfg, { configLoaded });
    if (unavailable && provider !== "atw_mail") {
      toast.error(unavailable);
      return;
    }

    setStarting(true);
    setOauthError(null);
    void (async () => {
      const saved = await startAgentPersonalMailConnect(agent.id, provider);
      if (!saved) {
        setStarting(false);
        toast.error("Could not start this agent's email connection.");
        return;
      }
      setPickedProvider(provider);
      toast.success(
        `${agentMailProviderLabel(provider)} connect started. Redirecting…`,
      );
      window.location.assign(personalMailOAuthStartUrl(agent.id, provider));
    })();
  };

  const cancelConnect = () => {
    if (!canEdit) return;
    void (async () => {
      clearPersonalMailOAuthHandoff(agent.id);
      setOauthError(null);
      const saved = await clearAgentPersonalMail(agent.id);
      if (!saved) {
        toast.error("Could not clear this agent's email connection.");
        return;
      }
      setPickedProvider(null);
      toast.success("Agent personal email cleared.");
    })();
  };

  const toggleSharedInbox = (inboxId: string) => {
    if (!canEdit) return;
    const next = new Set(agent.sharedInboxIds ?? []);
    if (next.has(inboxId)) next.delete(inboxId);
    else next.add(inboxId);
    const saved = setAgentSharedInboxes(agent.id, [...next]);
    if (!saved) {
      toast.error("Could not save shared inboxes for this agent.");
      return;
    }
    toast.success("Shared inboxes saved on this agent.");
  };

  if (pane === "group") {
    return (
      <div className="space-y-4">
        <p className="max-w-2xl text-sm text-muted-foreground">
          Link team shared inboxes for this agent. Shared means a team inbox, not a
          person&apos;s personal email.
        </p>

        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Link group / shared inboxes</CardTitle>
            <CardDescription>
              Link team shared inboxes already connected under Connections Email. This is not
              sharing a person&apos;s personal email with the agent.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4 text-sm">
            {org.sharedInboxes.length === 0 ? (
              <div className="space-y-3 text-muted-foreground">
                <p>
                  No group / shared inboxes are connected yet. Add a team address on Connections
                  Email, then link it here.
                </p>
                <Link
                  to={agentsHref("/connections/email")}
                  className="inline-flex h-8 items-center rounded-md border border-gray-300 px-3 text-xs font-semibold text-gray-900 hover:bg-gray-50 dark:border-white/20 dark:text-white dark:hover:bg-white/10"
                >
                  Open Connections Email
                </Link>
              </div>
            ) : (
              <>
                <ul className="space-y-4">
                  {org.sharedInboxes.map((inbox) => {
                    const checked = selectedShared.has(inbox.id);
                    const caps = normalizeEmailCapabilities(
                      agent.sharedInboxCapabilities?.[inbox.id],
                    );
                    return (
                      <li
                        key={inbox.id}
                        className="rounded-md border border-gray-200 px-3 py-3 dark:border-white/10"
                      >
                        <label className="flex items-start gap-2">
                          <input
                            type="checkbox"
                            className="mt-1"
                            checked={checked}
                            disabled={!canEdit}
                            onChange={() => toggleSharedInbox(inbox.id)}
                          />
                          <span>
                            <span className="font-medium text-gray-800 dark:text-gray-200">
                              {inbox.name}
                            </span>
                            <span className="mt-0.5 block text-muted-foreground">
                              {inbox.emailAddress}
                            </span>
                          </span>
                        </label>
                        {checked ? (
                          <div className="mt-3 pl-6">
                            <CapabilityToggles
                              value={caps}
                              disabled={!canEdit}
                              onChange={(next) => {
                                const saved = setAgentSharedInboxCapabilities(
                                  agent.id,
                                  inbox.id,
                                  next,
                                );
                                if (!saved) {
                                  toast.error("Could not save capabilities.");
                                  return;
                                }
                                toast.success("Shared inbox capabilities saved.");
                              }}
                            />
                          </div>
                        ) : null}
                      </li>
                    );
                  })}
                </ul>
                <p className="text-muted-foreground">
                  Need another team inbox?{" "}
                  <Link
                    to={agentsHref("/connections/email")}
                    className="underline underline-offset-2"
                  >
                    Add one on Connections Email
                  </Link>
                  .
                </p>
              </>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <p className="max-w-2xl text-sm text-muted-foreground">
        Give this agent its own email so it can work independently as part of the team. Connect
        the agent&apos;s mailbox here. This connection belongs to this agent only.
      </p>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Connect agent email</CardTitle>
          <CardDescription>
            Sign this agent into its own mailbox here. This connection belongs to this agent
            only, not the organization shared Connections list.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4 text-sm text-muted-foreground">
          {isConnected && mailbox ? (
            <div className="space-y-3">
              <p className="text-gray-900 dark:text-white">
                Status: <span className="font-medium">connected</span>
                {activeProvider
                  ? ` via ${agentMailProviderLabel(activeProvider)}`
                  : ""}
                . Signed in as <span className="font-medium">{mailbox}</span>.
              </p>
              <p className="text-xs">
                Only provider and mailbox metadata are saved in this browser. Sign-in tokens stay
                on the server for this agent.
              </p>
              {canEdit ? (
                <Button type="button" size="sm" variant="outline" onClick={cancelConnect}>
                  Disconnect
                </Button>
              ) : (
                <p>A Super Admin can change this agent email.</p>
              )}
              <CapabilityToggles
                value={personalCaps}
                disabled={!canEdit}
                onChange={(next) => {
                  const saved = setAgentPersonalEmailCapabilities(agent.id, next);
                  if (!saved) {
                    toast.error("Could not save capabilities.");
                    return;
                  }
                  toast.success("Agent email capabilities saved.");
                }}
              />
            </div>
          ) : null}

          {isPending && activeProvider ? (
            <div className="space-y-3 rounded-md border border-amber-200 bg-amber-50/60 px-3 py-3 dark:border-amber-500/30 dark:bg-amber-500/10">
              <p className="text-gray-900 dark:text-white">
                Status: <span className="font-medium">pending</span>
                {" · "}
                {agentMailProviderLabel(activeProvider)}
              </p>
              {oauthError ? (
                <p className="text-gray-900 dark:text-white">
                  Connect did not finish: {oauthError}
                </p>
              ) : (
                <p>
                  Sign-in for this agent is started but not finished. Finish in the provider window,
                  or continue below. This is not connected yet.
                </p>
              )}
              {canEdit ? (
                <div className="flex flex-wrap gap-2">
                  <Button
                    type="button"
                    size="sm"
                    disabled={starting}
                    onClick={() => startConnect(activeProvider)}
                  >
                    {starting
                      ? "Starting…"
                      : agentMailProviderOption(activeProvider)?.connectButtonLabel ||
                        "Continue"}
                  </Button>
                  <Button type="button" size="sm" variant="outline" onClick={cancelConnect}>
                    Cancel pending
                  </Button>
                </div>
              ) : null}
            </div>
          ) : null}

          {!isConnected ? (
            <div className="space-y-3">
              {status === "disconnected" ? (
                <p>
                  Choose this agent&apos;s email server, then start the connection. Starting a
                  provider sets{" "}
                  <span className="font-medium text-gray-800 dark:text-gray-200">pending</span>
                  , then launches OAuth (or ATW session bind). Connected only after a successful
                  callback.
                </p>
              ) : null}

              {configLoaded && !mailConfig ? (
                <p className="rounded-md border border-gray-200 bg-gray-50/80 px-3 py-2 text-gray-800 dark:border-white/10 dark:bg-white/5 dark:text-gray-200">
                  Email provider status could not be loaded right now. Try again in a moment.
                </p>
              ) : null}

              {canEdit ? (
                <>
                  <div className="space-y-2">
                    <p className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                      Email server
                    </p>
                    <ProviderPicker
                      picked={pickedProvider}
                      disabled={isPending}
                      onPick={setPickedProvider}
                      providerConfigs={mailConfig?.providers ?? null}
                      configLoaded={configLoaded}
                    />
                  </div>

                  {pickedProvider && pickedOption && !isPending && configLoaded ? (
                    <ProviderConnectPanel
                      provider={pickedProvider}
                      config={mailConfig?.providers?.[pickedProvider]}
                      starting={starting}
                      canEdit={canEdit}
                      configLoaded={configLoaded}
                      onStart={() => startConnect(pickedProvider)}
                    />
                  ) : null}
                </>
              ) : (
                <p>A Super Admin can connect agent email.</p>
              )}
            </div>
          ) : null}
        </CardContent>
      </Card>
    </div>
  );
}
